import React from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import { NativeSelect } from '../ui/NativeSelect';
import { Search, Filter, X, SortAsc, SortDesc } from 'lucide-react';
import { TemplateCategory, TEMPLATE_CATEGORIES } from '@/types/lists';

export interface TemplateFilters {
  search?: string;
  category?: TemplateCategory[];
}

export interface TemplateSort {
  field: 'title' | 'category' | 'usageCount' | 'updatedAt' | 'createdAt';
  direction: 'asc' | 'desc';
}

interface TemplateFiltersProps {
  filters: TemplateFilters;
  sort: TemplateSort;
  onFiltersChange: (filters: TemplateFilters) => void;
  onSortChange: (sort: TemplateSort) => void;
  onClearFilters: () => void;
  totalCount: number;
  filteredCount: number;
}

const sortOptions = [
  { field: 'title', label: 'Title' },
  { field: 'category', label: 'Category' },
  { field: 'usageCount', label: 'Usage Count' },
  { field: 'updatedAt', label: 'Updated' },
  { field: 'createdAt', label: 'Created' },
] as const;

export function TemplateFiltersComponent({
  filters,
  sort,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalCount,
  filteredCount
}: TemplateFiltersProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = React.useState(false);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined });
  };

  const toggleCategory = (category: TemplateCategory) => {
    const currentCategories = filters.category || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];

    onFiltersChange({
      ...filters,
      category: newCategories.length > 0 ? newCategories : undefined
    });
  };

  const handleSortChange = (field: TemplateSort['field']) => {
    if (sort.field === field) {
      onSortChange({
        field,
        direction: sort.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      onSortChange({ field, direction: 'asc' });
    }
  };

  const hasActiveFilters = !!(
    filters.search ||
    filters.category?.length
  );

  const getActiveFiltersCount = () => {
    return [
      filters.search ? 1 : 0,
      filters.category?.length || 0,
    ].reduce((a, b) => a + b, 0);
  };

  return (
    <Card>
      <CardContent className="filters-content">
        {/* Mobile: Compact toggle button */}
        <div className="filters-mobile">
          <div className="layout-row-between">
            <span className="text-muted-sm">
              Showing {filteredCount} of {totalCount} templates
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters & Sort
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Desktop: Always visible controls */}
        <div className="filters-desktop">
          {/* Search and Sort Row */}
          <div className="filters-row">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search templates..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="layout-row-gap">
              <NativeSelect
                value={sort.field}
                onChange={(e) => handleSortChange(e.target.value as TemplateSort['field'])}
              >
                {sortOptions.map(option => (
                  <option key={option.field} value={option.field}>
                    Sort by {option.label}
                  </option>
                ))}
              </NativeSelect>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
              >
                {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
              </Button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>
          </div>

          {/* Results Summary */}
          <div className="filters-summary">
            <span>
              Showing {filteredCount} of {totalCount} templates
            </span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-xs"
              >
                <X className="icon-sm mr-1" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Desktop Expanded Filters */}
          {isExpanded && (
            <div className="filters-expanded">
              {/* Categories */}
              <div className="filters-section">
                <label className="text-label">Categories</label>
                <div className="filters-buttons">
                  {TEMPLATE_CATEGORIES.map(category => (
                    <button
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className={`filters-btn ${
                        filters.category?.includes(category) ? 'filters-btn-active' : ''
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Expanded filters */}
        {isMobileFiltersOpen && (
          <div className="filters-mobile filters-expanded">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search templates..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="filters-section">
              <label className="text-label">Sort</label>
              <div className="layout-row-gap">
                <NativeSelect
                  value={sort.field}
                  onChange={(e) => handleSortChange(e.target.value as TemplateSort['field'])}
                  className="flex-1"
                >
                  {sortOptions.map(option => (
                    <option key={option.field} value={option.field}>
                      Sort by {option.label}
                    </option>
                  ))}
                </NativeSelect>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
                >
                  {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
                </Button>
              </div>
            </div>

            {/* Categories */}
            <div className="filters-section">
              <label className="text-label">Categories</label>
              <div className="filters-buttons">
                {TEMPLATE_CATEGORIES.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`filters-btn ${
                      filters.category?.includes(category) ? 'filters-btn-active' : ''
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <div className="pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  className="text-xs"
                >
                  <X className="icon-sm mr-1" />
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
